import { useState, useEffect, useRef } from "react";
import Translator from "../../classes/Translator/Translator";
import ProfileService from "./profile.service";
import CustomError from "../../classes/CustomError/CustomError";
import Storage from "../../classes/Storage/Storage";

const ProfileLogic = (validate) => {
  const [current, setCurrent] = useState(-1);
  const { translator } = Translator();
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmNewPassword: false,
    fullRegistration: false,
    currentPasswordFromStorage: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [cellphone, setCellphoneState] = useState("");
  const [cellphoneSaved, setCellphoneSaved] = useState(false);
  const [successCellphone, setSuccessCellphone] = useState(false);
  const [errorCellphone, setErrorCellphone] = useState("");
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [successMFA, setSuccessMFA] = useState(false);
  const [errorMFA, setErrorMFA] = useState("");

  const initialCellphoneRef = useRef("");

  useEffect(() => {
    ProfileService()
      .getUser()
      .then((response) => {
        if (!(response instanceof CustomError)) {
          const userData = response.response.data;
          const phone = userData.cellphone || "";
          setCellphoneState(phone);
          initialCellphoneRef.current = phone;
          setMfaEnabled(userData.mfa_enabled ?? false);
          setCellphoneSaved(!!phone);
        }
      })
      .catch(() => {
        setMfaEnabled(false);
        setCellphoneSaved(false);
      });
  }, []);

  const refreshUserFromServer = async () => {
    try {
      const response = await ProfileService().getUser();
      if (response instanceof CustomError) {
        return null;
      }
      const userData = response.response?.data || {};
      const phone = userData.cellphone || "";
      setCellphoneState(phone);
      initialCellphoneRef.current = phone;
      setMfaEnabled(userData.mfa_enabled ?? false);
      setCellphoneSaved(!!phone);
      return userData;
    } catch {
      return null;
    }
  };

  const handleCellphoneSubmit = async (phoneArg) => {
    const phone =
      typeof phoneArg === "string" ? phoneArg.trim() : cellphone.trim();

    if (!phone) {
      setErrorCellphone(
        "Debes ingresar un número de teléfono antes de guardar."
      );
      setSuccessCellphone(false);
      setCellphoneSaved(false);
      setTimeout(() => setErrorCellphone(""), 3000);
      return;
    }

    try {
      const response = await ProfileService().updateCellphone(phone);
      if (response instanceof CustomError) {
        setErrorCellphone(response.message || "Error guardando el número");
        setSuccessCellphone(false);
        setCellphoneSaved(false);
        setTimeout(() => setErrorCellphone(""), 3000);
        return;
      }

      const userData = await refreshUserFromServer();
      if (userData && userData.cellphone) {
        setSuccessCellphone(true);
        setErrorCellphone("");
        setCellphoneSaved(true);
        initialCellphoneRef.current = userData.cellphone;
        setTimeout(() => setSuccessCellphone(false), 3000);
      } else {
        setErrorCellphone(
          "El número no quedó guardado en servidor. Intenta de nuevo."
        );
        setSuccessCellphone(false);
        setCellphoneSaved(false);
        setTimeout(() => setErrorCellphone(""), 4000);
      }
    } catch {
      setErrorCellphone("Error inesperado actualizando el número");
      setSuccessCellphone(false);
      setCellphoneSaved(false);
      setTimeout(() => setErrorCellphone(""), 3000);
    }
  };

  const setCellphoneAndMarkDirty = (value) => {
    setCellphoneState(value);
    if (value && value === initialCellphoneRef.current) {
      setCellphoneSaved(true);
    } else {
      setCellphoneSaved(false);
    }
  };

  const handleMFAToggle = async (enabled) => {
    if (!cellphoneSaved) {
      setErrorMFA(
        "Debe guardar un número de teléfono válido antes de poder activar MFA."
      );
      setTimeout(() => setErrorMFA(""), 4000);
      setMfaEnabled((prev) => prev);
      return;
    }

    try {
      const response = await ProfileService().updateMFA(enabled);

      if (response instanceof CustomError) {
        const backendMsg =
          response.error_data?.response?.data?.errors ||
          response.message ||
          null;

        if (
          backendMsg &&
          typeof backendMsg === "string" &&
          backendMsg.includes("User does not have a phone number")
        ) {
          setErrorMFA(
            "El servidor indica que no hay número de teléfono. Guarda el número correctamente e intenta de nuevo."
          );
        } else {
          setErrorMFA(response.message || "Error actualizando MFA");
        }

        setSuccessMFA(false);
        setTimeout(() => setErrorMFA(""), 4000);
        return;
      }

      if (response.status === 204 || response.success === true) {
        setSuccessMFA(true);
        setErrorMFA("");
        setTimeout(() => setSuccessMFA(false), 3000);
        setMfaEnabled(enabled);
        return;
      }

      if (response && response.wasSuccessful) {
        setSuccessMFA(true);
        setErrorMFA("");
        setMfaEnabled(enabled);
        setTimeout(() => setSuccessMFA(false), 3000);
        return;
      }

      setErrorMFA("Respuesta inesperada del servidor al actualizar MFA.");
      setSuccessMFA(false);
      setTimeout(() => setErrorMFA(""), 4000);
    } catch {
      setErrorMFA("Error inesperado actualizando MFA");
      setSuccessMFA(false);
      setTimeout(() => setErrorMFA(""), 3000);
    }
  };

  const changeState = (panel) => (e, newValue) => {
    setCurrent(newValue ? panel : -1);
  };

  const changePassword = async () => {
    const userId = new Storage("id").getItem();
    const { currentPassword, newPassword } = values;
    const passwords = { currentPassword, newPassword };
    const response = await ProfileService().changePassword(userId, passwords);
    if (response instanceof CustomError) {
      setErrors({ ...errors, api_error: response.message });
    } else {
      setValues((prev) => ({ ...prev, fullRegistration: true }));
    }
  };

  useEffect(() => {
    const hasAllFields =
      values.currentPassword && values.newPassword && values.confirmNewPassword;

    const hasErrors =
      Object.keys(errors).length > 0 ||
      values.newPassword !== values.confirmNewPassword;

    setButtonDisabled(!(hasAllFields && !hasErrors));
  }, [values, errors]);

  useEffect(() => {
    if (isSubmitting) {
      changePassword();
      setErrors(typeof validate === "function" ? validate(values) : {});
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => {
      const newValues = {
        ...prev,
        [name]: value,
      };

      if (
        newValues.newPassword &&
        newValues.confirmNewPassword &&
        newValues.newPassword !== newValues.confirmNewPassword
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          error_no_match_new_password: "Las contraseñas no coinciden.",
        }));
      } else {
        setErrors((prevErrors) => {
          const { error_no_match_new_password, ...rest } = prevErrors;
          return rest;
        });
      }

      return newValues;
    });
  };

  const handleClickShowCurrentPassword = () => {
    setValues((prev) => ({
      ...prev,
      showCurrentPassword: !prev.showCurrentPassword,
    }));
  };

  const handleClickShowNewPassword = () => {
    setValues((prev) => ({
      ...prev,
      showNewPassword: !prev.showNewPassword,
    }));
  };

  const handleClickShowConfirmNewPassword = () => {
    setValues((prev) => ({
      ...prev,
      showConfirmNewPassword: !prev.showConfirmNewPassword,
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return {
    values,
    current,
    changeState,
    errors,
    setErrors,
    setValues,
    buttonDisabled,
    translator,
    cellphone,
    setCellphone: setCellphoneAndMarkDirty,
    handleCellphoneSubmit,
    successCellphone,
    errorCellphone,
    mfaEnabled,
    handleMFAToggle,
    successMFA,
    errorMFA,
    handleChange,
    handleClickShowCurrentPassword,
    handleClickShowNewPassword,
    handleClickShowConfirmNewPassword,
    handleMouseDownPassword,
    cellphoneSaved,
  };
};

export { ProfileLogic };
